import type {
  Doctor,
  Money as MoneyAmount,
  PerformedProcedure,
  PerformedProcedureStatus,
  TreatmentSettlementStatus,
} from "@clinic/shared";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Ltr, MenuItem, Money, PersonName, RowMenu, type IconName } from "@clinic/ui";
import {
  netPrice,
  nextStatuses,
  statusTone,
  treatmentSurfaces,
  treatmentTeeth,
} from "@web/modules/patients/lib/treatments/treatments";
import { useRegionLabel } from "@web/modules/patients/hooks/use-region-label";
import { treatmentRegions } from "@web/modules/patients/lib/chart/body-regions";
import { SETTLEMENT_STATUS_TONES } from "@web/shared/constants/settlement";
import { formatDate, formatList } from "@web/shared/lib/format";
import { afterLabCost } from "@web/shared/lib/lab-cost";

const MOVE_ICONS: Record<PerformedProcedureStatus, IconName> = {
  planned: "reset",
  in_progress: "clock",
  done: "check",
  cancelled: "x",
};

export interface TreatmentItemProps {
  readonly treatment: PerformedProcedure;
  readonly name: string;
  readonly doctor: Doctor | undefined;
  readonly currency: string | undefined;
  readonly showPrice: boolean;
  readonly mayChange: boolean;
  readonly mayDelete: boolean;
  readonly onMove: (status: PerformedProcedureStatus) => void;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onSendToLab?: (() => void) | undefined;
  readonly doctorPay?: readonly TreatmentSettlementStatus[] | undefined;
  readonly labCost?: MoneyAmount | undefined;
}

export function TreatmentItem({
  treatment,
  name,
  doctor,
  currency,
  showPrice,
  mayChange,
  mayDelete,
  onMove,
  onEdit,
  onDelete,
  onSendToLab,
  doctorPay,
  labCost,
}: TreatmentItemProps): JSX.Element {
  const { t } = useTranslation();
  const teeth = treatmentTeeth(treatment);
  const surfaces = treatmentSurfaces(treatment);
  const regionLabel = useRegionLabel();
  const regions = treatmentRegions(treatment).map(regionLabel);
  const cancelled = treatment.status === "cancelled";
  const hasLabCost = showPrice && labCost !== undefined && Number(labCost) !== 0;
  const hasMenu = mayChange || mayDelete || onSendToLab !== undefined;

  return (
    <li
      data-testid={`treatment-${treatment.id}`}
      className="flex flex-col gap-1.5 rounded-panel border border-line bg-surface p-3"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p
            className={
              cancelled
                ? "truncate text-value font-medium text-ink-muted line-through"
                : "truncate text-value font-medium text-ink"
            }
          >
            {name}
          </p>
        </div>

        <Badge
          tone={statusTone(treatment.status)}
          data-testid="treatment-status"
          className="shrink-0"
        >
          {t(`chart.procedureStatus.${treatment.status}`)}
        </Badge>

        {hasMenu && (
          <RowMenu label={t("treatments.menu")} data-testid={`treatment-${treatment.id}-menu`}>
            {mayChange &&
              nextStatuses(treatment.status).map((status) => (
                <MenuItem
                  key={status}
                  icon={MOVE_ICONS[status]}
                  data-testid={`treatment-move-${status}`}
                  onSelect={() => onMove(status)}
                >
                  {t(`treatments.moveTo.${status}`)}
                </MenuItem>
              ))}
            {mayChange && (
              <MenuItem icon="edit" data-testid="treatment-edit" onSelect={onEdit}>
                {t("common.edit")}
              </MenuItem>
            )}
            {onSendToLab && (
              <MenuItem icon="clipboard" data-testid="treatment-send-to-lab" onSelect={onSendToLab}>
                {t("labs.sendToLab")}
              </MenuItem>
            )}
            {mayDelete && (
              <MenuItem
                icon="trash"
                tone="danger"
                data-testid="treatment-delete"
                onSelect={onDelete}
              >
                {t("common.delete")}
              </MenuItem>
            )}
          </RowMenu>
        )}
      </div>

      <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-meta text-ink-muted">
        {teeth.length > 0 && (
          <div className="flex gap-1">
            <dt>{t("visits.teeth")}:</dt>
            <dd>
              <Ltr>{teeth.join(" · ")}</Ltr>
              {surfaces.length > 0 &&
                ` (${formatList(surfaces.map((surface) => t(`chart.surfaces.${surface}`)))})`}
            </dd>
          </div>
        )}
        {regions.length > 0 && (
          <div className="flex gap-1">
            <dt>{t("chart.body.region")}:</dt>
            <dd data-testid="treatment-regions">{formatList(regions)}</dd>
          </div>
        )}
        <div className="flex gap-1">
          <dt>{t("chart.panel.date")}:</dt>
          <Ltr as="dd">{formatDate(treatment.performedAt)}</Ltr>
        </div>
        <div className="flex gap-1">
          <dt>{t("chart.panel.doctor")}:</dt>
          <dd>
            <PersonName name={doctor?.user.name} />
          </dd>
        </div>
        {showPrice && (
          <div className="flex gap-1">
            <dt>{t("chart.panel.price")}:</dt>
            <dd>
              <Money amount={netPrice(treatment)} currency={currency} />
              {Number(treatment.discount) !== 0 && treatment.discountReason && (
                <span> · {treatment.discountReason}</span>
              )}
            </dd>
          </div>
        )}
        {hasLabCost && (
          <>
            <div className="flex gap-1" data-testid="treatment-lab-cost">
              <dt>{t("treatments.labCost")}:</dt>
              <dd>
                <Money amount={labCost} currency={currency} />
              </dd>
            </div>
            <div className="flex gap-1" data-testid="treatment-after-lab">
              <dt>{t("treatments.afterLab")}:</dt>
              <dd className="font-medium text-ink">
                <Money amount={afterLabCost(netPrice(treatment), labCost)} currency={currency} />
              </dd>
            </div>
          </>
        )}
      </dl>

      {doctorPay && doctorPay.length > 0 && (
        <ul data-testid="treatment-doctor-pay" className="flex flex-wrap gap-1.5">
          {doctorPay.map((pay) => (
            <li key={pay.doctorId}>
              <Badge tone={SETTLEMENT_STATUS_TONES[pay.status]}>
                <PersonName name={pay.doctorName} />
                {" · "}
                {t(`treatments.doctorPay.${pay.status}`)}
                {pay.lastPaidAt && pay.status === "paid" && ` · ${formatDate(pay.lastPaidAt)}`}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {treatment.notes && (
        <p className="whitespace-pre-wrap text-meta text-ink">{treatment.notes}</p>
      )}
    </li>
  );
}
